import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import LibreriaEsercizi from './LibreriaEsercizi'
import TopBar from './TopBar'
import ModaleConferma from './ModaleConferma'
import { TimerSerie } from './TimerBlocco'
import { useIndietro } from '../lib/navigazioneIndietro'
import { caricoDi } from '../lib/formati'

/**
 * SESSIONE PERSONALE — tutto il flusso "allenati da solo" dell'atleta:
 * crea sessione → aggiungi esercizi (libreria) → esegui → salva → completa,
 * più lo storico (rinomina/riapri) e l'avvio rapido da un singolo esercizio.
 *
 * UNA RIGA PER SERIE. Prima tutto l'esercizio stava in un record solo, con un
 * campo "SET" che era un numero digitato: "3 set da 10". Delle tre serie non
 * restava traccia — quante ripetizioni nella terza, se il carico era calato —
 * e i progressi dell'allenamento da solo non si potevano confrontare con quelli
 * di gruppo, dove ogni serie è una riga sua. Ora anche qui ogni serie è una
 * riga, numerata, e si registra quando la si finisce.
 *
 * CRONOMETRO FACOLTATIVO. Negli esercizi a tempo si può avviare il countdown e
 * lasciare che registri la durata vera, oppure scrivere i secondi a mano. Qui
 * NON si impone come nel percorso col coach: lì il piano lo scrive un'altra
 * persona e il risultato torna a lei, qui si è soli, e obbligare il countdown
 * vorrebbe dire non poter registrare un allenamento già fatto col cronometro
 * del muro.
 *
 * NIENTE CANCELLAZIONI A SORPRESA. Di sessione personale ce n'è una per giorno,
 * e prima qualunque strada che ne cominciasse una nuova cancellava in silenzio
 * quella aperta con tutte le sue serie: si era a metà allenamento, si toccava un
 * esercizio dal tab Esercizi, e il lavoro fatto spariva senza una parola. Ora
 * l'avvio rapido AGGIUNGE l'esercizio alla sessione aperta, e creare una
 * sessione nuova quando ce n'è una con serie dentro chiede prima cosa fare.
 *
 * Props (quelle che restano davvero condivise con HomeAtleta):
 *   profilo               — profilo atleta corrente
 *   richiesta              { tipo: 'nuova'|'storico'|'riprendi'|'rapido', sessione?, ex? }
 *                           Come aprire la vista al mount. Letta una sola volta:
 *                           HomeAtleta monta questo componente da zero ogni volta
 *                           (key diversa), quindi qui basta uno useEffect con [].
 *   eserciziFavoriti, tracking   — dati condivisi, sola lettura
 *   ultimiCarichi, caricaUltimiCarichi — stato/funzione condivisi con altre viste (progressi, gruppo)
 *   onChiudi()             — chiude del tutto questa vista, torna alla home
 *   onCambiaTab(tab)        — per tornare al tab "Esercizi" quando la sessione era partita da lì
 *   onEsecuzioneAttiva(bool) — per sopprimere lo sponsor interstitial durante l'esecuzione
 *   onDatiCambiati()        — ricarica i dati della home (sessPersonali, tracking, ecc.)
 *   onNotifiche()           — apre PaginaNotifiche
 *   conferma, setConferma   — modale di conferma condivisa con il resto della home
 *   toast, toastMsg         — toast condiviso con il resto della home
 */

/**
 * Le voci esercizio di una sessione, normalizzate.
 *
 * Questa mappatura stava scritta in quattro punti identici (avvio rapido,
 * ripresa, riapertura, indietro dall'esecuzione): quattro copie da tenere
 * allineate a mano ogni volta che si aggiunge un campo.
 */
function vociEsercizi(lista) {
  return (lista || []).map(e => ({
    id: e.id,
    nome: e.nome,
    tipo_misura: e.tipo_misura || 'ripetizioni',
    attrezzatura: e.attrezzatura || [],
    varianti: e.varianti || [],
    regressioni: e.regressioni || [],
    consenteElastico: !!e.consenteElastico,
    variantiAttrezzo: e.variantiAttrezzo || [],
  }))
}

export default function SessionePersonale({
  profilo,
  richiesta,
  sessPersonali = [],
  setSessPersonali,
  eserciziFavoriti = [],
  tracking = [],
  ultimiCarichi = {},
  caricaUltimiCarichi,
  onChiudi,
  onCambiaTab,
  onEsecuzioneAttiva,
  onDatiCambiati,
  onNotifiche,
  conferma,
  setConferma,
  toast,
  toastMsg,
}) {
  const sessPartitaDaEsercizi = richiesta?.tipo === 'rapido'
  const [sessCompletataMsg, setSessCompletataMsg] = useState(false)
  const [confermaSalva, setConfermaSalva] = useState(null)
  // Sessione aperta con serie dentro, che una nuova cancellerebbe: si chiede prima.
  const [confermaSostituzione, setConfermaSostituzione] = useState(null) // { sessione, nSerie }
  const [sessPersonaleStep, setSessPersonaleStep] = useState('lista')
  const [sessPersonaleEsercizi, setSessPersonaleEsercizi] = useState([])
  const [sessPersonaleInCorso, setSessPersonaleInCorso] = useState(null)
  const [sessPersonaleRisultati, setSessPersonaleRisultati] = useState({})
  // Serie già registrate, per esercizio: { [esercizioId]: [riga, ...] }
  const [serieSalvate, setSerieSalvate] = useState({})
  // Esercizio per cui è in corso un salvataggio: il pulsante si disabilita.
  // Senza questo, due tocchi rapidi — normali su un telefono, col fiato corto —
  // producevano due righe con lo stesso numero di serie.
  const [salvandoSerie, setSalvandoSerie] = useState(null)
  const [timerSerie, setTimerSerie] = useState(null)
  const [sessPersonaleLibreria, setSessPersonaleLibreria] = useState(false)
  const [sessPersonaleNome, setSessPersonaleNome] = useState('')
  const [spDettaglio, setSpDettaglio] = useState(null)
  const [spDettaglioTracking, setSpDettaglioTracking] = useState([])
  const [spDettaglioLoading, setSpDettaglioLoading] = useState(false)
  const [spDettaglioFromHome, setSpDettaglioFromHome] = useState(false)
  const [videoPersonali, setVideoPersonali] = useState({}) // {esercizioId: link_video_personale}, per mostrarli durante l'esecuzione

  function toast_msg(msg) { toastMsg && toastMsg(msg) }

  /** Le serie registrate per un esercizio, in ordine. */
  function serieDi(exId) { return serieSalvate[exId] || [] }

  // Carica i link video personali (esercizi_preferiti) per mostrarli sulla card durante l'esecuzione
  useEffect(() => {
    async function caricaVideoPersonali() {
      const { data } = await supabase.from('esercizi_preferiti')
        .select('esercizio_id, link_video_personale')
        .eq('utente_id', profilo.id)
        .not('link_video_personale', 'is', null)
      const mappa = {}
      ;(data || []).forEach(p => { if (p.link_video_personale) mappa[p.esercizio_id] = p.link_video_personale })
      setVideoPersonali(mappa)
    }
    caricaVideoPersonali()
  }, [profilo.id])

  // Segnala al genitore se siamo in esecuzione, per sopprimere lo sponsor interstitial
  useEffect(() => {
    onEsecuzioneAttiva && onEsecuzioneAttiva(sessPersonaleStep === 'esecuzione')
    return () => onEsecuzioneAttiva && onEsecuzioneAttiva(false)
  }, [sessPersonaleStep, onEsecuzioneAttiva])

  // ── CHIUSURE CONDIVISE: freccia dell'app e tasto indietro usano le stesse funzioni ──

  function indietroEsecuzione() {
    tentaEscita(() => {
      if (sessPartitaDaEsercizi) {
        // Sessione avviata dal tab bar "Esercizi": la freccia riporta lì, non alla schermata "Cosa fai oggi?"
        onChiudi && onChiudi()
        if (onCambiaTab) onCambiaTab('esercizi')
        return
      }
      if (sessPersonaleInCorso?.esercizi) {
        setSessPersonaleEsercizi(vociEsercizi(sessPersonaleInCorso.esercizi))
      }
      setSessPersonaleStep('crea')
    })
  }

  function indietroDettaglio() {
    setSpDettaglio(null)
    setSpDettaglioTracking([])
    if (spDettaglioFromHome) { setSpDettaglioFromHome(false); onChiudi && onChiudi() }
  }

  // Tasto indietro sul componente intero: fa ciò che farebbe la freccia della
  // schermata visibile in quel momento. Restituisce false quando la sessione
  // personale resta aperta (cambio di step, o avviso di conferma mostrato),
  // così il tasto indietro successivo continua a funzionare.
  // Libreria e dettaglio storico hanno registrazioni proprie, più sotto nella pila.
  useIndietro(true, () => {
    if (timerSerie) { setTimerSerie(null); return false }                     // chiude il cronometro
    if (confermaSostituzione) { setConfermaSostituzione(null); return false } // = "Annulla"
    if (confermaSalva) { setConfermaSalva(null); return false }               // = "Torna alla sessione"
    if (sessCompletataMsg) { chiudiSessCompletata(); return }
    if (sessPersonaleStep === 'esecuzione') { indietroEsecuzione(); return false }
    if (sessPersonaleStep === 'lista' || sessPersonaleStep === 'storico') { setSessPersonaleStep('crea'); return false }
    onChiudi && onChiudi()                                                    // step 'crea': esce
  })
  useIndietro(sessPersonaleLibreria, () => setSessPersonaleLibreria(false))
  useIndietro(!!spDettaglio, indietroDettaglio)

  // Apertura mirata al mount, in base a come si è arrivati qui
  useEffect(() => {
    (async () => {
      switch (richiesta?.tipo) {
        case 'rapido':
          await avviaRapido(richiesta.ex, richiesta.ex.carico, richiesta.ex.elastico, richiesta.ex.variantiAttrezzo)
          break
        case 'storico':
          setSessPersonaleStep('storico')
          setSpDettaglioFromHome(true)
          await apriDettaglioSessPersonale(richiesta.sessione)
          break
        case 'riprendi':
          await apriInEsecuzione(richiesta.sessione)
          break
        case 'nuova':
        default:
          await iniziaSessPersonale()
      }
    })()
  }, [])

  /** Carica una sessione e va dritti all'esecuzione. */
  async function apriInEsecuzione(sp) {
    setSessPersonaleInCorso(sp)
    setSessPersonaleEsercizi(vociEsercizi(sp.esercizi))
    setSessPersonaleRisultati({})
    caricaUltimiCarichi && caricaUltimiCarichi((sp.esercizi || []).map(e => e.id).filter(Boolean))
    setSerieSalvate(await caricaSerieSalvate(sp))
    setSessPersonaleStep('esecuzione')
  }

  // ── AVVIO RAPIDO: un solo esercizio, senza passare dallo step "Cosa fai oggi?" ──
  //
  // Se c'è già una sessione aperta oggi l'esercizio le viene AGGIUNTO. Prima la
  // sessione aperta veniva cancellata con tutte le sue serie, in silenzio: chi
  // era a metà allenamento e toccava un esercizio dalla libreria perdeva tutto.
  // Aggiungere è anche quello che l'atleta vuole davvero in quel momento.
  async function avviaRapido(ex, caricoIniziale, elasticoIniziale, variantiAttrezzo) {
    const oggi2 = new Date().toISOString().split('T')[0]
    const voce = {
      id: ex.id, nome: ex.nome, tipo_misura: ex.tipo_misura || 'ripetizioni',
      attrezzatura: ex.attrezzatura || [], varianti: ex.varianti || [], regressioni: ex.regressioni || [],
      consenteElastico: !!ex.consente_elastico, variantiAttrezzo: variantiAttrezzo || [],
    }
    const risIniziali = elasticoIniziale ? { [ex.id]: { usaElastico: true, elastico: elasticoIniziale } }
      : caricoIniziale ? { [ex.id]: { kg: caricoIniziale } }
      : {}

    const aperta = await sessioneIncompiutaDiOggi(oggi2)
    if (aperta) {
      const giaPresente = (aperta.esercizi || []).some(e => e.id === ex.id)
      let sessione = aperta
      if (!giaPresente) {
        const { data, error } = await supabase.from('sessioni_personali')
          .update({ esercizi: [...(aperta.esercizi || []), voce] })
          .eq('id', aperta.id).select().single()
        if (error) { toast_msg('Errore: ' + error.message); return }
        sessione = data
      }
      setSessPersonaleInCorso(sessione)
      setSessPersonaleEsercizi(vociEsercizi(sessione.esercizi))
      setSessPersonaleRisultati(risIniziali)
      setSerieSalvate(await caricaSerieSalvate(sessione))
      caricaUltimiCarichi && caricaUltimiCarichi((sessione.esercizi || []).map(e => e.id).filter(Boolean))
      setSessPersonaleStep('esecuzione')
      if (!giaPresente) toast_msg(`➕ ${ex.nome} aggiunto alla sessione di oggi`)
      return
    }

    const { data: nuova, error } = await supabase.from('sessioni_personali').insert({
      atleta_id: profilo.id,
      data: oggi2,
      nome: null,
      completata: false,
      esercizi: [voce],
    }).select().single()
    if (error) { toast_msg('Errore: ' + error.message); return }
    setSessPersonaleInCorso(nuova)
    setSessPersonaleEsercizi([voce])
    setSessPersonaleRisultati(risIniziali)
    setSerieSalvate({})
    caricaUltimiCarichi && caricaUltimiCarichi([ex.id])
    setSessPersonaleStep('esecuzione')
  }

  // ── SESSIONE PERSONALE ──
  async function iniziaSessPersonale() {
    const oggi2 = new Date().toISOString().split('T')[0]
    const esistente = await sessioneDiOggi(oggi2)
    if (esistente && !esistente.completata) {
      // Sessione in corso non completata: riprendi dallo step crea (con nome già precompilato)
      setSessPersonaleInCorso(esistente)
      setSessPersonaleEsercizi(vociEsercizi(esistente.esercizi))
      setSessPersonaleNome(esistente.nome || '')
      setSerieSalvate(await caricaSerieSalvate(esistente))
      setSessPersonaleStep('crea')
    } else {
      // Nessuna sessione oggi (o già completata): si va dritti alla creazione.
      // Prima si passava da una schermata intermedia con un solo pulsante
      // ("Crea sessione di oggi"): un tocco in più che non faceva scegliere nulla.
      // Lo storico resta raggiungibile dal sub-tab "Storico" dentro la creazione.
      setSessPersonaleInCorso(null)
      setSessPersonaleEsercizi([])
      setSessPersonaleRisultati({})
      setSerieSalvate({})
      setSessPersonaleNome('')
      setSessPersonaleStep('crea')
    }
  }

  async function creaSessPersonale() {
    if (sessPersonaleEsercizi.length === 0) return
    const oggi2 = new Date().toISOString().split('T')[0]

    if (sessPersonaleInCorso?.id) {
      // Sessione già esistente (es. tornati indietro dall'esecuzione): aggiorna esercizi nel DB
      const esercizi = sessPersonaleEsercizi.map(e => ({
        id: e.id, nome: e.nome, tipo_misura: e.tipo_misura, attrezzatura: e.attrezzatura || [],
        varianti: e.varianti || [], regressioni: e.regressioni || [],
        consenteElastico: !!e.consenteElastico, variantiAttrezzo: e.variantiAttrezzo || [],
      }))
      // Mantieni lo stato "completato" per gli esercizi già salvati, resetta per i nuovi
      const eserciziFusione = esercizi.map(e => {
        const esistente = (sessPersonaleInCorso.esercizi || []).find(ex => ex.id === e.id)
        return esistente ? esistente : e
      })
      const { data: aggiornata, error } = await supabase
        .from('sessioni_personali')
        .update({ esercizi: eserciziFusione })
        .eq('id', sessPersonaleInCorso.id)
        .select()
        .single()
      if (error) { toast_msg('Errore: ' + error.message); return }
      setSessPersonaleInCorso(aggiornata)
      caricaUltimiCarichi && caricaUltimiCarichi(esercizi.map(e => e.id))
      setSerieSalvate(await caricaSerieSalvate(aggiornata))
      setSessPersonaleStep('esecuzione')
      return
    }

    // Nuova sessione: se oggi ce n'è una aperta CON serie dentro, prima si chiede.
    const aperta = await sessioneIncompiutaDiOggi(oggi2)
    if (aperta) {
      const serie = await caricaSerieSalvate(aperta)
      const nSerie = Object.values(serie).reduce((tot, lista) => tot + lista.length, 0)
      if (nSerie > 0) { setConfermaSostituzione({ sessione: aperta, nSerie }); return }
    }
    await creaNuovaSostituendo(aperta)
  }

  /** Crea la sessione di oggi, eliminando quella aperta (se c'è) con le sue serie. */
  async function creaNuovaSostituendo(aperta) {
    const oggi2 = new Date().toISOString().split('T')[0]
    const esercizi = sessPersonaleEsercizi.map(e => ({
      id: e.id, nome: e.nome, tipo_misura: e.tipo_misura, attrezzatura: e.attrezzatura || [],
      varianti: e.varianti || [], regressioni: e.regressioni || [],
      consenteElastico: !!e.consenteElastico, variantiAttrezzo: e.variantiAttrezzo || [],
    }))
    if (aperta) {
      const ok = await eliminaSessioneConSerie(aperta)
      if (!ok) return
    }
    const { data: nuova, error } = await supabase.from('sessioni_personali').insert({
      atleta_id: profilo.id,
      data: oggi2,
      nome: sessPersonaleNome.trim() || null,
      completata: false,
      esercizi,
    }).select().single()
    if (error) { toast_msg('Errore: ' + error.message); return }
    setConfermaSostituzione(null)
    setSessPersonaleInCorso(nuova)
    setSerieSalvate({})
    caricaUltimiCarichi && caricaUltimiCarichi(esercizi.map(e => e.id))
    setSessPersonaleStep('esecuzione')
  }

  /**
   * Cancella una sessione personale E le serie che vi erano state registrate.
   *
   * Da quando ogni serie si salva nell'istante in cui la registri, cancellare
   * la sola riga della sessione lasciava tutte le serie nei progressi: la
   * modale prometteva "la sessione e i risultati inseriti verranno eliminati"
   * e l'atleta si ritrovava comunque in archivio un allenamento che credeva
   * cancellato.
   *
   * Restituisce false se il database rifiuta: chi chiama non deve proseguire
   * come se fosse andata, perché prima l'esito non veniva guardato.
   */
  async function eliminaSessioneConSerie(sessione) {
    if (!sessione?.id) return true
    const esIds = (sessione.esercizi || []).map(e => e.id).filter(Boolean)
    if (esIds.length > 0 && sessione.data) {
      const { error } = await supabase.from('tracking_esercizio').delete()
        .eq('atleta_id', profilo.id)
        .eq('data', sessione.data)
        .eq('fonte', 'personale')
        .in('esercizio_id', esIds)
      if (error) { toast_msg('Errore: ' + error.message); return false }
    }
    const { error } = await supabase.from('sessioni_personali').delete().eq('id', sessione.id)
    if (error) { toast_msg('Errore: ' + error.message); return false }
    return true
  }

  /** La sessione di oggi, completata o no. Con .limit(1) invece di maybeSingle:
   *  nessun vincolo a database garantisce una sola riga per giorno, e maybeSingle
   *  va in ERRORE se ne trova due — fallendo in silenzio, perché l'esito non
   *  veniva controllato. Così invece si prende la più recente e si va avanti. */
  async function sessioneDiOggi(oggi2) {
    const { data } = await supabase.from('sessioni_personali')
      .select('*').eq('atleta_id', profilo.id).eq('data', oggi2)
      .order('creato_il', { ascending: false }).limit(1)
    return (data || [])[0] || null
  }

  /** La sessione di oggi lasciata a metà, se c'è. */
  async function sessioneIncompiutaDiOggi(oggi2) {
    const { data } = await supabase.from('sessioni_personali')
      .select('*').eq('atleta_id', profilo.id).eq('data', oggi2).eq('completata', false)
      .order('creato_il', { ascending: false }).limit(1)
    return (data || [])[0] || null
  }

  async function eliminaSessPersonale() {
    if (!sessPersonaleInCorso) return
    const ok = await eliminaSessioneConSerie(sessPersonaleInCorso)
    if (!ok) return
    const daEsercizi = sessPartitaDaEsercizi
    onChiudi && onChiudi()
    if (daEsercizi && onCambiaTab) onCambiaTab('esercizi')
    onDatiCambiati && onDatiCambiati()
    toast_msg('Sessione eliminata')
  }

  /**
   * Registra UNA serie. Ogni chiamata aggiunge una riga: non si sovrascrive mai
   * la precedente, perché la seconda serie non è una correzione della prima.
   * durataForzata arriva dal cronometro ed è la durata davvero eseguita.
   */
  async function salvaSerieEsercizio(ex, durataForzata = null) {
    if (salvandoSerie) return   // un salvataggio alla volta: vedi salvandoSerie
    const r = sessPersonaleRisultati[ex.id] || {}
    const oggi2 = new Date().toISOString().split('T')[0]
    const fatte = serieDi(ex.id).length

    const durata = ex.tipo_misura === 'tempo' ? (durataForzata ?? (r.durata || null)) : null
    const ripetizioni = ex.tipo_misura !== 'tempo' ? (r.rip || null) : null
    if (!durata && !ripetizioni) {
      toast_msg(ex.tipo_misura === 'tempo' ? 'Inserisci i secondi o avvia il cronometro' : 'Inserisci le ripetizioni')
      return
    }

    setSalvandoSerie(ex.id)
    try {
      const { data: nuova, error } = await supabase.from('tracking_esercizio').insert({
        atleta_id: profilo.id,
        esercizio_id: ex.id,
        data: oggi2,
        serie_numero: fatte + 1,
        ripetizioni,
        durata_secondi: durata,
        // Stessa funzione che riempie il campo a schermo: vedi lib/formati.js.
        // Qui il campo era già controllato, quindi il difetto del carico
        // perduto non c'era; quello che cambia è che adesso il campo arriva
        // precaricato con l'ultimo peso, come nella sessione di gruppo, e il
        // valore mostrato e quello salvato restano un numero solo.
        ...caricoDi(r, ultimiCarichi[ex.id]),
        fonte: 'personale',
      }).select().single()
      if (error) { toast_msg('Serie NON salvata: ' + error.message); return }

      setSerieSalvate(prev => ({ ...prev, [ex.id]: [...(prev[ex.id] || []), nuova] }))
      toast_msg(`✅ ${ex.nome} · serie ${fatte + 1}`)
      await segnaCompletato(ex.id, true)
    } finally {
      setSalvandoSerie(null)
    }
  }

  /** Toglie una serie registrata per sbaglio e rinumera quelle dopo. */
  async function eliminaSerieSalvata(ex, riga) {
    const { error } = await supabase.from('tracking_esercizio').delete().eq('id', riga.id)
    if (error) { toast_msg('Serie NON eliminata: ' + error.message); return }
    const restanti = serieDi(ex.id).filter(s => s.id !== riga.id)
    // Rinumerare serve: senza, dopo aver tolto la seconda di tre si resterebbe
    // con le serie 1 e 3 e la prossima registrata sarebbe di nuovo la 3.
    const rinumerate = restanti.map((s, i) => ({ ...s, serie_numero: i + 1 }))
    const esiti = await Promise.all(rinumerate.map(s =>
      supabase.from('tracking_esercizio').update({ serie_numero: s.serie_numero }).eq('id', s.id)))
    setSerieSalvate(prev => ({ ...prev, [ex.id]: rinumerate }))
    if (rinumerate.length === 0) await segnaCompletato(ex.id, false)
    const rinumerazioneFallita = esiti.some(e => e.error)
    toast_msg(rinumerazioneFallita
      ? 'Serie eliminata, ma la rinumerazione non è riuscita'
      : 'Serie eliminata')
  }

  /** Marca l'esercizio come fatto (o non fatto) dentro la sessione salvata. */
  async function segnaCompletato(exId, completato) {
    if (!sessPersonaleInCorso) return
    const esAggiornati = (sessPersonaleInCorso.esercizi || []).map(e =>
      e.id === exId ? { ...e, completato } : e)
    const { error } = await supabase.from('sessioni_personali')
      .update({ esercizi: esAggiornati }).eq('id', sessPersonaleInCorso.id)
    if (error) { console.error('[sessione personale] segnaCompletato:', error); return }
    setSessPersonaleInCorso(s => ({ ...s, esercizi: esAggiornati }))
  }

  // Cambia la variante attrezzo (es. Lat Machine → presa inversa) direttamente in fase di esecuzione,
  // senza tornare alla libreria: sposta gli eventuali risultati già digitati sul nuovo id
  // e aggiorna sia lo stato locale che la sessione salvata a DB.
  // Le serie GIÀ registrate restano dov'erano: sono il racconto fedele di quello
  // che è stato fatto con quell'attrezzo, e spostarle lo falsificherebbe.
  async function cambiaVarianteAttrezzo(vecchioId, variante) {
    if (!variante || variante.id === vecchioId) return
    const attuale = sessPersonaleEsercizi.find(e => e.id === vecchioId)
    const nuovoItem = {
      id: variante.id, nome: variante.nome, tipo_misura: variante.tipo_misura || 'ripetizioni',
      attrezzatura: variante.attrezzatura || [], varianti: [], regressioni: [],
      consenteElastico: !!variante.consente_elastico,
      variantiAttrezzo: attuale?.variantiAttrezzo || [],
    }
    setSessPersonaleEsercizi(prev => prev.map(e => e.id === vecchioId ? nuovoItem : e))
    setSessPersonaleRisultati(prev => {
      const { [vecchioId]: risVecchio, ...resto } = prev
      return risVecchio ? { ...resto, [variante.id]: risVecchio } : prev
    })
    if (sessPersonaleInCorso) {
      const esAggiornati = (sessPersonaleInCorso.esercizi || []).map(e => e.id === vecchioId ? { ...nuovoItem, completato: e.completato } : e)
      const { error } = await supabase.from('sessioni_personali').update({ esercizi: esAggiornati }).eq('id', sessPersonaleInCorso.id)
      if (error) { toast_msg('Variante non salvata: ' + error.message); return }
      setSessPersonaleInCorso(s => ({ ...s, esercizi: esAggiornati }))
    }
    caricaUltimiCarichi && caricaUltimiCarichi([variante.id])
  }

  async function completaSessPersonale() {
    if (!sessPersonaleInCorso) return
    const { error } = await supabase.from('sessioni_personali')
      .update({ completata: true, completata_il: new Date().toISOString() })
      .eq('id', sessPersonaleInCorso.id)
    // Senza questo controllo la schermata "🎉 Sessione completata!" compariva
    // anche quando il database aveva rifiutato l'aggiornamento, e la sessione
    // restava aperta senza che l'atleta lo sapesse.
    if (error) { toast_msg('Sessione NON completata: ' + error.message); return }
    setSessCompletataMsg(true)
    onDatiCambiati && onDatiCambiati()
  }

  function chiudiSessCompletata() {
    setSessCompletataMsg(false)
    onChiudi && onChiudi()
  }

  function tentaEscita(callbackSeEsci) {
    // Se sessione in corso e non completata, chiede conferma
    if (sessPersonaleInCorso && !sessPersonaleInCorso.completata) {
      setConfermaSalva(() => callbackSeEsci)
    } else {
      callbackSeEsci()
    }
  }

  function toggleEsercizioSess(ex) {
    setSessPersonaleEsercizi(prev => {
      const esiste = prev.find(e => e.id === ex.id)
      if (esiste) return prev.filter(e => e.id !== ex.id)
      return [...prev, { id: ex.id, nome: ex.nome, tipo_misura: ex.tipo_misura || 'ripetizioni', attrezzatura: ex.attrezzatura || [], varianti: ex.varianti || [], regressioni: ex.regressioni || [], consenteElastico: !!ex.consente_elastico }]
    })
  }

  // ── DETTAGLIO SESSIONE PERSONALE (sola lettura) ──
  async function apriDettaglioSessPersonale(sp) {
    setSpDettaglio(sp)
    setSpDettaglioLoading(true)
    const esIds = (sp.esercizi || []).map(e => e.id).filter(Boolean)
    if (esIds.length === 0) { setSpDettaglioLoading(false); return }
    // Carica tutti i tracking di quella data con fonte personale
    const { data } = await supabase.from('tracking_esercizio')
      .select('*')
      .eq('atleta_id', profilo.id)
      .in('esercizio_id', esIds)
      .eq('data', sp.data)
      .eq('fonte', 'personale')
      .order('creato_il', { ascending: true })
    setSpDettaglioTracking(data || [])
    setSpDettaglioLoading(false)
  }

  /**
   * Le serie registrate per ogni esercizio della sessione.
   *
   * Prima questa funzione restituiva UN valore per esercizio, perché una riga
   * c'era e basta. Ora restituisce la lista: serve a mostrare le serie fatte e
   * a sapere che numero dare alla prossima.
   */
  async function caricaSerieSalvate(sessione) {
    const esIds = (sessione?.esercizi || []).map(e => e.id).filter(Boolean)
    if (esIds.length === 0 || !sessione?.data) return {}
    const { data } = await supabase.from('tracking_esercizio').select('*')
      .eq('atleta_id', profilo.id)
      .eq('data', sessione.data)
      .eq('fonte', 'personale')
      .in('esercizio_id', esIds)
      .order('creato_il', { ascending: true })
    const mappa = {}
    ;(data || []).forEach(r => {
      if (!mappa[r.esercizio_id]) mappa[r.esercizio_id] = []
      mappa[r.esercizio_id].push(r)
    })
    return mappa
  }

  async function riapriSessPersonale(sp) {
    const { data: aggiornata, error } = await supabase.from('sessioni_personali')
      .update({ completata: false, completata_il: null }).eq('id', sp.id).select().single()
    if (error) { toast_msg('Errore: ' + error.message); return }
    setSpDettaglio(null)
    setSpDettaglioTracking([])
    setSpDettaglioFromHome(false)
    await apriInEsecuzione(aggiornata)
    onDatiCambiati && onDatiCambiati()
    toast_msg('🔓 Sessione riaperta')
  }

  function setRisultato(exId, campo, val) {
    setSessPersonaleRisultati(prev => ({ ...prev, [exId]: { ...(prev[exId] || {}), [campo]: val } }))
  }

  /** Una serie registrata, in parole: "12 rip · 20kg" oppure "45s". */
  function descriviSerie(r) {
    const parti = []
    if (r.durata_secondi != null) parti.push(`${r.durata_secondi}s`)
    if (r.ripetizioni != null) parti.push(`${r.ripetizioni} rip`)
    if (r.elastico) parti.push(`🦺 ${r.elastico}`)
    else if (r.peso > 0) parti.push(`${r.peso}kg`)
    // Righe salvate prima del passaggio a una riga per serie: il conteggio stava qui.
    if (r.serie_numero == null && r.sets) parti.push(`${r.sets} set insieme`)
    return parti.join(' · ') || '—'
  }

  // Cronometro a schermo intero di una singola serie
  if (timerSerie) return (
    <TimerSerie
      secondi={timerSerie.secondi}
      nomeEsercizio={timerSerie.ex.nome}
      serieNumero={timerSerie.serieNumero}
      onAnnulla={() => setTimerSerie(null)}
      onCompletata={async (durataEseguita) => {
        const t = timerSerie
        await salvaSerieEsercizio(t.ex, durataEseguita)
        setTimeout(() => setTimerSerie(null), 1600)
      }}
    />
  )

  // Sessione aperta che una nuova cancellerebbe: si sceglie cosa fare.
  // Prima non si scegliva niente: la cancellazione avveniva e basta.
  if (confermaSostituzione) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }} className="fade-in">
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 28, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>⚠️</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, textAlign: 'center', marginBottom: 10 }}>
            Hai già una sessione aperta oggi
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.7, marginBottom: 24 }}>
            Contiene <strong style={{ color: 'var(--text)' }}>
              {confermaSostituzione.nSerie} {confermaSostituzione.nSerie === 1 ? 'serie registrata' : 'serie registrate'}
            </strong>. Crearne una nuova le cancella.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn-primary" style={{ fontSize: 15 }}
              onClick={async () => { const sp = confermaSostituzione.sessione; setConfermaSostituzione(null); await apriInEsecuzione(sp) }}>
              ↩︎ Riprendi quella aperta
            </button>
            <button className="btn btn-secondary" style={{ fontSize: 14 }}
              onClick={() => setConfermaSostituzione(null)}>
              Annulla
            </button>
            <button className="btn btn-ghost" style={{ fontSize: 13, color: 'var(--red)', borderColor: 'rgba(248,113,113,0.3)' }}
              onClick={() => creaNuovaSostituendo(confermaSostituzione.sessione)}>
              🗑️ Cancella quella aperta e ricomincia
            </button>
          </div>
        </div>
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  // Schermata completamento sessione personale
  if (sessCompletataMsg) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }} className="fade-in">
      <div style={{ fontSize: 72, marginBottom: 20 }}>🎉</div>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 26, marginBottom: 10 }}>Sessione completata!</div>
      <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 8 }}>
        Ottimo lavoro! Tutti i risultati sono stati registrati.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, width: '100%', maxWidth: 320 }}>
        {(sessPersonaleInCorso?.esercizi || []).map((ex, i) => {
          const n = serieDi(ex.id).length
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', borderRadius: 12, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <span style={{ color: ex.completato ? 'var(--green)' : 'var(--muted)', fontSize: 16 }}>{ex.completato ? '✓' : '○'}</span>
              <span style={{ fontSize: 13, fontWeight: 500, flex: 1, textAlign: 'left' }}>{ex.nome}</span>
              {n > 0 && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{n} {n === 1 ? 'serie' : 'serie'}</span>}
            </div>
          )
        })}
      </div>
      <button className="btn btn-primary" onClick={chiudiSessCompletata} style={{ marginTop: 28, fontSize: 16, padding: '16px', maxWidth: 320, width: '100%' }}>
        Torna alla home →
      </button>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  // Modale conferma uscita senza completare
  if (confermaSalva) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }} className="fade-in">
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 28, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>⚠️</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, textAlign: 'center', marginBottom: 10 }}>Sessione non completata</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.7, marginBottom: 24 }}>
            Hai una sessione in corso. Se esci adesso la sessione rimarrà aperta ma non verrà segnata come completata.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn-primary" onClick={() => {
              setConfermaSalva(null)
              completaSessPersonale()
            }} style={{ fontSize: 15 }}>
              🎉 Completa e salva la sessione
            </button>
            <button className="btn btn-ghost" onClick={() => {
              const cb = confermaSalva
              setConfermaSalva(null)
              setSessPersonaleInCorso(null)
              setSessPersonaleEsercizi([])
              setSessPersonaleRisultati({})
              setSerieSalvate({})
              setSessPersonaleStep('lista')
              cb()
            }} style={{ fontSize: 14, color: 'var(--muted)' }}>
              Esci senza completare
            </button>
            <button className="btn btn-secondary" onClick={() => setConfermaSalva(null)} style={{ fontSize: 14 }}>
              Torna alla sessione
            </button>
          </div>
        </div>
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  if (sessPersonaleLibreria) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      {/* Nessuna TopBar qui: LibreriaEsercizi ha già la propria intestazione con
          la freccia indietro. Prima se ne impilavano tre, con due frecce diverse
          nella stessa schermata e un terzo dello spazio occupato prima di vedere
          un esercizio. */}
      {sessPersonaleEsercizi.length > 0 && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'rgba(127,214,61,0.08)' }}>
          <button className="btn btn-primary" style={{ fontSize: 14, padding: '10px', width: '100%' }}
            onClick={() => setSessPersonaleLibreria(false)}>
            ✓ Fatto — {sessPersonaleEsercizi.length} {sessPersonaleEsercizi.length === 1 ? 'esercizio scelto' : 'esercizi scelti'}
          </button>
        </div>
      )}
      <LibreriaEsercizi
        modaleSelezione
        esercizioSelezionato={(ex) => toggleEsercizioSess({ id: ex.id, nome: ex.nome, tipo_misura: ex.tipo_misura })}
        esercizioSelezionatoIds={sessPersonaleEsercizi.map(e => e.id)}
        onChiudi={() => setSessPersonaleLibreria(false)}
      />
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  if (sessPersonaleStep === 'crea') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar
        onIndietro={() => onChiudi && onChiudi()}
        onNotifiche={onNotifiche}
      />
      <div className="scroll-area">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginTop: 2 }}>Cosa fai oggi?</div>
          </div>
          {/* Lo storico era raggiungibile solo dalla schermata intermedia ora saltata */}
          <button onClick={() => setSessPersonaleStep('storico')}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 11px', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Syne', flexShrink: 0 }}>
            🗓 Storico
          </button>
        </div>
        {eserciziFavoriti.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="card-title">⭐ I tuoi esercizi</div>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Tocca per selezionare</span>
            </div>
            <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {eserciziFavoriti.map(ex => {
                const sel = sessPersonaleEsercizi.find(e => e.id === ex.id)
                const ultimoRec = tracking.find(t => t.esercizio_id === ex.id)
                return (
                  <div key={ex.id} onClick={() => toggleEsercizioSess(ex)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12,
                      background: sel ? 'rgba(127,214,61,0.1)' : 'var(--surface2)',
                      border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: sel ? 'var(--accent)' : 'var(--surface)',
                      border: `2px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {sel && <span style={{ fontSize: 12, color: '#000', fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{ex.nome}</div>
                      {ultimoRec && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          Ultima: {ultimoRec.ripetizioni ? `${ultimoRec.ripetizioni} rip` : ultimoRec.durata_secondi ? `${ultimoRec.durata_secondi}s` : '—'}
                          {ultimoRec.peso > 0 && ` · ${ultimoRec.peso}kg`}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <button onClick={() => setSessPersonaleLibreria(true)} className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 20 }}>📚</span>
          <span>Cerca nella libreria completa</span>
          <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontSize: 16 }}>›</span>
        </button>
        {sessPersonaleEsercizi.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="card-title">Esercizi selezionati</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setSessPersonaleLibreria(true)}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '3px 9px', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  ✏️ Modifica
                </button>
                <span className="badge badge-accent">{sessPersonaleEsercizi.length}</span>
              </div>
            </div>
            <div style={{ padding: '0 16px' }}>
              {sessPersonaleEsercizi.map((ex, i) => (
                <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < sessPersonaleEsercizi.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{ex.nome}</div>
                  <button onClick={() => toggleEsercizioSess(ex)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16 }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Il nome è opzionale: sta dopo la scelta degli esercizi, che è invece
            obbligatoria. Prima occupava la posizione migliore della schermata. */}
        {sessPersonaleEsercizi.length > 0 && (
          <div className="form-group">
            <label style={{ fontSize: 13, fontWeight: 700, fontFamily: 'Syne', color: 'var(--text)', marginBottom: 4, display: 'block' }}>
              ✏️ Nome sessione <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12 }}>(opzionale)</span>
            </label>
            <input className="form-input" value={sessPersonaleNome} onChange={e => setSessPersonaleNome(e.target.value)} placeholder="es. Forza upper, Animal Flow..." style={{ fontSize: 15 }} />
          </div>
        )}
        {sessPersonaleEsercizi.length > 0 && (
          <button className="btn btn-primary" onClick={creaSessPersonale} style={{ fontSize: 16, padding: '16px', marginTop: 4 }}>
            💪 Inizia allenamento
          </button>
        )}
        {sessPersonaleEsercizi.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', fontSize: 13, color: 'var(--muted)', background: 'var(--surface)', borderRadius: 12, border: '1px dashed var(--border)' }}>
            Seleziona almeno un esercizio per continuare
          </div>
        )}
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  if (sessPersonaleStep === 'esecuzione') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar
        onIndietro={indietroEsecuzione}
        onNotifiche={onNotifiche}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px 0' }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18 }}>
          {sessPersonaleInCorso?.nome || (sessPersonaleEsercizi.length === 1 ? sessPersonaleEsercizi[0].nome : 'Sessione personale')}
        </div>
        <button onClick={() => setConferma({ titolo: 'Eliminare la sessione?', testo: 'La sessione e i risultati inseriti verranno eliminati. Lo storico dei progressi già salvati resta invariato.', testoConferma: 'Elimina sessione', icona: '🗑️', distruttiva: true, onConferma: () => { setConferma(null); eliminaSessPersonale() } })}
          style={{ background: 'none', border: 'none', borderRadius: 8, color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '4px 8px', flexShrink: 0 }}
          title="Elimina sessione" aria-label="Elimina sessione">🗑️</button>
      </div>
      <div className="scroll-area">
        {(sessPersonaleInCorso?.esercizi || []).filter(e => e.completato).length === 0 && (
          <div style={{ background: 'rgba(127,214,61,0.07)', border: '1px solid rgba(127,214,61,0.2)', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>💪</span>
            <div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>Pronto per allenarti?</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{sessPersonaleEsercizi.length} {sessPersonaleEsercizi.length === 1 ? 'esercizio' : 'esercizi'} · registra una serie alla volta</div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: 'var(--accent)', borderRadius: 2,
              width: `${Math.round(((sessPersonaleInCorso?.esercizi || []).filter(e => e.completato).length / Math.max(sessPersonaleEsercizi.length, 1)) * 100)}%`,
              transition: 'width 0.5s' }} />
          </div>
          <span style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
            {(sessPersonaleInCorso?.esercizi || []).filter(e => e.completato).length}/{sessPersonaleEsercizi.length}
          </span>
        </div>
        {sessPersonaleEsercizi.map((ex, i) => {
          const r = sessPersonaleRisultati[ex.id] || {}
          const serie = serieDi(ex.id)
          const completato = serie.length > 0
          const ultimoCarico = ultimiCarichi[ex.id]
          // Il carico da mostrare nei campi: lo stesso che salverà
          // salvaSerieEsercizio. Prima il campo KG partiva vuoto e bisognava
          // ribattere il peso a ogni allenamento, mentre nella sessione di
          // gruppo arrivava già scritto: due comportamenti diversi per lo
          // stesso gesto.
          const carico = caricoDi(r, ultimoCarico)
          const aTempo = ex.tipo_misura === 'tempo'
          const inSalvataggio = salvandoSerie === ex.id
          return (
            <div key={ex.id} className="card" style={{ border: completato ? '1px solid rgba(74,222,128,0.3)' : '1px solid var(--border)' }}>
              <div className="card-header" style={{ background: completato ? 'rgba(74,222,128,0.05)' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: completato ? 'var(--green)' : 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {completato ? <span style={{ color: '#000', fontSize: 12, fontWeight: 800 }}>✓</span> : <span style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 700 }}>{i + 1}</span>}
                  </div>
                  <div>
                    <div className="card-title">{ex.nome}</div>
                    {videoPersonali[ex.id] && (
                      <a href={videoPersonali[ex.id]} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        style={{ color: 'var(--accent2)', fontSize: 11, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        ▶ il mio video
                      </a>
                    )}
                  </div>
                </div>
                {serie.length > 0 && (
                  <span className="badge badge-green">{serie.length} {serie.length === 1 ? 'serie' : 'serie'}</span>
                )}
              </div>

              {/* Le serie già fatte, una riga per una. È il motivo di tutta questa
                  modifica: prima c'era solo "3 set" e di com'erano andate non
                  restava niente. */}
              {serie.length > 0 && (
                <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {serie.map((s, si) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
                      background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10 }}>
                      <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 11, color: 'var(--muted)', minWidth: 46 }}>
                        Serie {s.serie_numero ?? si + 1}
                      </span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{descriviSerie(s)}</span>
                      <button onClick={() => eliminaSerieSalvata(ex, s)} title="Elimina serie" aria-label="Elimina serie"
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ex.variantiAttrezzo && ex.variantiAttrezzo.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {ex.variantiAttrezzo.map(v => (
                      <button key={v.id} type="button" onClick={() => cambiaVarianteAttrezzo(ex.id, v)} className="tag"
                        style={v.id === ex.id ? { background: 'rgba(127,214,61,0.15)', borderColor: 'var(--accent)', color: 'var(--accent)' } : {}}>
                        {v.nome}
                      </button>
                    ))}
                  </div>
                )}
                {ultimoCarico && (
                  <div style={{ fontSize: 11, color: 'var(--accent2)', background: 'rgba(87,184,255,0.08)', borderRadius: 8, padding: '5px 10px', border: '1px solid rgba(87,184,255,0.15)' }}>
                    📊 Ultima: {ultimoCarico.elastico ? `🦺 ${ultimoCarico.elastico}` : ultimoCarico.peso > 0 ? `${ultimoCarico.peso}kg` : '—'}
                    {ultimoCarico.ripetizioni ? ` · ${ultimoCarico.ripetizioni} rip` : ''}
                    {ultimoCarico.durata_secondi ? ` · ${ultimoCarico.durata_secondi}s` : ''}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  {aTempo ? (
                    <div style={{ flex: 1 }}>
                      <input className="num-input" type="number" value={r.durata || ''} onChange={e => setRisultato(ex.id, 'durata', +e.target.value)} placeholder="0" />
                      <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>SECONDI</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <input className="num-input" type="number" value={r.rip || ''} onChange={e => setRisultato(ex.id, 'rip', +e.target.value)} placeholder="0" />
                        <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>RIP</div>
                      </div>
                      {!r.usaElastico ? (
                        <div style={{ flex: 1 }}>
                          <input className="num-input" type="number" value={carico.peso || ''} onChange={e => setRisultato(ex.id, 'kg', +e.target.value)} placeholder="0" />
                          <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>KG</div>
                        </div>
                      ) : (
                        <div style={{ flex: 2 }}>
                          <select className="form-select" style={{ fontSize: 12, padding: '8px' }} value={carico.elastico || ''} onChange={e => setRisultato(ex.id, 'elastico', e.target.value)}>
                            <option value="">-- elastico --</option>
                            {['Giallo (leggero)','Verde','Rosso','Blu','Viola','Nero (forte)'].map(c => <option key={c}>{c}</option>)}
                          </select>
                          <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>ELASTICO</div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Negli esercizi a tempo il cronometro è un'alternativa, non un
                    obbligo: da soli si può voler registrare un allenamento già
                    fatto, o averne usato uno diverso. Chi lo avvia non deve poi
                    scrivere niente: la durata eseguita la mette lui. */}
                {aTempo && (
                  <button className="btn btn-secondary"
                    onClick={() => setTimerSerie({
                      ex, serieNumero: serie.length + 1,
                      secondi: r.durata || ultimoCarico?.durata_secondi || 30,
                    })}
                    style={{ fontSize: 14, padding: 12 }}>
                    ▶ Avvia cronometro · {r.durata || ultimoCarico?.durata_secondi || 30}s
                  </button>
                )}

                <button className="btn btn-primary" onClick={() => salvaSerieEsercizio(ex)}
                  disabled={inSalvataggio}
                  style={{ fontSize: 14, padding: 12, opacity: inSalvataggio ? 0.6 : 1 }}>
                  {inSalvataggio ? '⏳ Salvataggio...' : `✓ Salva serie ${serie.length + 1}`}
                </button>

                {!aTempo && ex.consenteElastico && (
                  <button onClick={() => setRisultato(ex.id, 'usaElastico', !r.usaElastico)}
                    style={{ background: 'none', border: `1px solid ${r.usaElastico ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, color: r.usaElastico ? 'var(--accent)' : 'var(--muted)', fontSize: 11, padding: '4px 10px', cursor: 'pointer', alignSelf: 'flex-start' }}>
                    🦺 {r.usaElastico ? 'Usa kg' : 'Uso elastico'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
        {sessPersonaleEsercizi.length > 0 && (() => {
          const completati = sessPersonaleEsercizi.filter(e => serieDi(e.id).length > 0).length
          const tuttiCompletati = completati === sessPersonaleEsercizi.length
          return tuttiCompletati ? (
            <button className="btn btn-primary" onClick={completaSessPersonale} style={{ fontSize: 16, padding: '16px', marginTop: 8 }}>
              🎉 Completa sessione
            </button>
          ) : (
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={completaSessPersonale}
                style={{ fontSize: 14, borderColor: 'rgba(127,214,61,0.3)', color: 'var(--accent)' }}>
                ✓ Fine sessione ({completati}/{sessPersonaleEsercizi.length} {sessPersonaleEsercizi.length === 1 ? 'esercizio completato' : 'esercizi completati'})
              </button>
              <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 6 }}>
                Puoi terminare anche senza completare tutti gli esercizi
              </div>
            </div>
          )
        })()}
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  // ── DETTAGLIO SESSIONE PERSONALE (sola lettura) ──
  if (spDettaglio) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar
        onIndietro={indietroDettaglio}
        onNotifiche={onNotifiche}
      />
      <div className="scroll-area">
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
          {spDettaglio.nome || (spDettaglio.esercizi?.length === 1 ? spDettaglio.esercizi[0].nome : null) || new Date(spDettaglio.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        {/* Info sessione + campo nome modificabile */}
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {new Date(spDettaglio.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              {spDettaglio.completata_il && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                  ⏱ Completata alle {new Date(spDettaglio.completata_il).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
            <span className={`badge ${spDettaglio.completata ? 'badge-green' : 'badge-orange'}`}>
              {spDettaglio.completata ? '✓ Completata' : 'In corso'}
            </span>
          </div>
          {/* Nome sessione modificabile — rinomina la sessione per tua organizzazione, non tocca l'esercizio */}
          <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>✏️ Nome sessione (solo per te)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="form-input"
              defaultValue={spDettaglio.nome || ''}
              placeholder="Dai un nome a questa sessione..."
              style={{ fontSize: 13, padding: '8px 12px', flex: 1 }}
              onBlur={async (e) => {
                const nuovoNome = e.target.value.trim()
                if (nuovoNome === (spDettaglio.nome || '')) return
                const { error } = await supabase.from('sessioni_personali').update({ nome: nuovoNome || null }).eq('id', spDettaglio.id)
                // Prima il "✅ Nome aggiornato" compariva anche quando il
                // database aveva rifiutato, e il nome tornava quello di prima
                // al caricamento successivo.
                if (error) { toast_msg('Nome NON aggiornato: ' + error.message); return }
                setSpDettaglio(p => ({ ...p, nome: nuovoNome || null }))
                setSessPersonali && setSessPersonali(prev => prev.map(s => s.id === spDettaglio.id ? { ...s, nome: nuovoNome || null } : s))
                toast_msg('✅ Nome aggiornato')
              }}
            />
          </div>
        </div>

        {spDettaglio.completata && (
          <button className="btn btn-secondary" onClick={() => riapriSessPersonale(spDettaglio)} style={{ fontSize: 13 }}>
            🔓 Riapri sessione per correggere i risultati
          </button>
        )}

        {/* Esercizi con risultati */}
        {spDettaglioLoading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Caricamento...</div>
        ) : (
          (spDettaglio.esercizi || []).map((ex, j) => {
            const tracciati = spDettaglioTracking.filter(t => t.esercizio_id === ex.id)
            return (
              <div key={ex.id} className="card" style={{ border: tracciati.length > 0 ? '1px solid rgba(74,222,128,0.2)' : '1px solid var(--border)' }}>
                <div className="card-header" style={{ background: tracciati.length > 0 ? 'rgba(74,222,128,0.04)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: tracciati.length > 0 ? 'var(--green)' : 'var(--surface2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {tracciati.length > 0
                        ? <span style={{ color: '#000', fontSize: 11, fontWeight: 800 }}>✓</span>
                        : <span style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 700 }}>{j + 1}</span>}
                    </div>
                    <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14 }}>{ex.nome}</div>
                  </div>
                  {tracciati.length > 0 && (
                    <span className="badge badge-green">{tracciati.length} {tracciati.length === 1 ? 'serie' : 'serie'}</span>
                  )}
                </div>
                {tracciati.length > 0 ? (
                  <div style={{ padding: '6px 16px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {tracciati.map((t, ti) => {
                      const parti = []
                      if (t.durata_secondi != null) parti.push({ label: 'Durata', val: `${t.durata_secondi}s`, color: 'var(--accent2)' })
                      if (t.ripetizioni != null) parti.push({ label: 'Rip', val: t.ripetizioni, color: 'var(--accent)' })
                      if (t.elastico) parti.push({ label: 'Elastico', val: `🦺 ${t.elastico}`, color: 'var(--purple)' })
                      else if (t.peso > 0) parti.push({ label: 'Kg', val: `${t.peso}kg`, color: 'var(--accent3)' })
                      // Le righe salvate prima del passaggio a una riga per serie
                      // tenevano il conteggio qui: si mostra com'era, senza riscriverlo.
                      if (t.serie_numero == null && t.sets != null) parti.push({ label: 'Set', val: t.sets, color: 'var(--yellow)' })
                      return (
                        <div key={t.id} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '4px 0',
                          borderBottom: ti < tracciati.length - 1 ? '1px dashed rgba(255,255,255,0.05)' : 'none' }}>
                          <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 10, color: 'var(--muted)', minWidth: 46 }}>
                            {t.serie_numero != null ? `Serie ${t.serie_numero}` : 'Registrato'}
                          </span>
                          {parti.map(p => (
                            <span key={p.label} style={{ fontSize: 12, background: 'var(--surface2)', borderRadius: 6,
                              padding: '3px 8px', color: p.color, fontWeight: 700 }}>
                              <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, marginRight: 3 }}>{p.label}</span>{p.val}
                            </span>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                ) : ex.completato ? (
                  <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>
                    Esercizio completato — nessun dato registrato
                  </div>
                ) : (
                  <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--muted)' }}>
                    Non completato
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar
        onIndietro={() => setSessPersonaleStep('crea')}
        onNotifiche={onNotifiche}
      />
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
        {[{ id: 'lista', label: 'Oggi' }, { id: 'storico', label: 'Storico' }].map(t => (
          <button key={t.id} onClick={() => setSessPersonaleStep(t.id)} style={{ padding: '9px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${sessPersonaleStep === t.id ? 'var(--accent)' : 'transparent'}`, color: sessPersonaleStep === t.id ? 'var(--accent)' : 'var(--muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Syne' }}>{t.label}</button>
        ))}
      </div>
      <div className="scroll-area">
        {sessPersonaleStep === 'storico' ? (
          sessPersonali.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Nessuna sessione</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Le tue sessioni personali appariranno qui</div>
            </div>
          ) : sessPersonali.map((sp) => (
            <div key={sp.id} className="card" onClick={() => apriDettaglioSessPersonale(sp)}
              style={{ cursor: 'pointer', border: sp.completata ? '1px solid rgba(74,222,128,0.2)' : '1px solid var(--border)' }}>
              <div className="card-header">
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14 }}>
                    {sp.nome || (sp.esercizi?.length === 1 ? sp.esercizi[0].nome : null) || new Date(sp.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {sp.nome && new Date(sp.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {sp.nome && ' · '}
                    {(sp.esercizi || []).length} {(sp.esercizi || []).length === 1 ? 'esercizio' : 'esercizi'}
                    {sp.completata_il && ` · ${new Date(sp.completata_il).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`badge ${sp.completata ? 'badge-green' : 'badge-orange'}`}>
                    {sp.completata ? '✓ Completata' : 'In corso'}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 16 }}>›</span>
                </div>
              </div>
              <div style={{ padding: '8px 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(sp.esercizi || []).map((ex, j) => (
                  <span key={j} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                    border: '1px solid var(--border)',
                    background: ex.completato ? 'rgba(74,222,128,0.1)' : 'var(--surface2)',
                    color: ex.completato ? 'var(--green)' : 'var(--muted)' }}>
                    {ex.completato ? '✓ ' : ''}{ex.nome}
                  </span>
                ))}
              </div>
            </div>
          ))
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginTop: 2 }}>Allenati da solo</div>
            <button className="btn btn-primary" onClick={() => {
              setSessPersonaleInCorso(null)
              setSessPersonaleEsercizi([])
              setSessPersonaleRisultati({})
              setSerieSalvate({})
              setSessPersonaleNome('')
              setSessPersonaleStep('crea')
            }} style={{ fontSize: 16, padding: '16px' }}>
              ➕ Crea sessione di oggi
            </button>
            {eserciziFavoriti.length > 0 && (
              <div className="card">
                <div className="card-header"><div className="card-title">⭐ I tuoi esercizi monitorati</div></div>
                <div style={{ padding: '0 16px' }}>
                  {eserciziFavoriti.map((ex, i) => {
                    const ultimoRec = tracking.find(t => t.esercizio_id === ex.id)
                    return (
                      <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < eserciziFavoriti.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{ex.nome}</div>
                        <div style={{ fontSize: 12, textAlign: 'right' }}>
                          {ultimoRec?.ripetizioni && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{ultimoRec.ripetizioni} rip</span>}
                          {ultimoRec?.durata_secondi && <span style={{ color: 'var(--accent2)', fontWeight: 700 }}>{ultimoRec.durata_secondi}s</span>}
                          {ultimoRec?.peso > 0 && <span style={{ color: 'var(--accent3)' }}> · {ultimoRec.peso}kg</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
